import React, { useState } from 'react';
import Icon from '../Icon';

type TreeProps = {
	treeData: TreeNode[];
	selectableFromDepth?: number;
	onSelect: (entry: TreeNode, depth: number) => void;
	selectedEntryId?: string;
};

const Tree: React.FC<TreeProps> = ({ onSelect, treeData, selectedEntryId }) => {
	const [expandedNodes, setExpandedNodes] = useState<string[]>([]);

	const toggleNode = (nodeId: string) => {
		setExpandedNodes((prevExpandedNodes) => {
			const expandedNodesCopy = [...prevExpandedNodes];

			if (expandedNodesCopy.includes(nodeId)) {
				// Node is currently expanded, collapse it
				return expandedNodesCopy.filter((id) => id !== nodeId);
			}

			// Node is currently collapsed, expand it and collapse its adjacent nodes
			const parentNode = findParentNode(treeData, nodeId);
			const parentChildren = parentNode ? parentNode.children : treeData;
			if (parentChildren) {
				const adjacentChildIds = parentChildren
					.filter((childNode) => childNode.id !== nodeId)
					.map((childNode) => childNode.id);

				return [
					...expandedNodesCopy.filter((id) => !adjacentChildIds.includes(id)),
					nodeId,
				];
			}

			return [...expandedNodesCopy, nodeId];
		});
	};

	const findNodeById = (
		nodes: TreeNode[],
		id: string
	): TreeNode | undefined => {
		for (const node of nodes) {
			if (node.id === id) {
				return node;
			}
			if (node.children) {
				const foundNode = findNodeById(node.children, id);
				if (foundNode) {
					return foundNode;
				}
			}
		}
		return undefined;
	};

	const findParentNode = (
		nodes: TreeNode[],
		childId: string
	): TreeNode | undefined => {
		for (const node of nodes) {
			if (node.children) {
				for (const childNode of node.children) {
					if (childNode.id === childId) {
						return node;
					}
					const parentNode = findParentNode(childNode.children || [], childId);
					if (parentNode) {
						return parentNode;
					}
				}
			}
		}
		return undefined;
	};

	const renderNode = (node: TreeNode, depth = 0) => {
		const isExpanded = expandedNodes.includes(node.id);
		const paddingLeft = depth * 20;

		const hasChildren = node.children && node.children.length > 0;

		return (
			<div
				key={node.id}
				style={{
					paddingLeft,
				}}>
				<div
					onClick={() => !isExpanded && hasChildren && toggleNode(node.id)}
					style={{
						display: 'flex',
						alignItems: 'center',
						gap: '5px',
					}}>
					{hasChildren && (
						<span
							onClick={() => isExpanded && hasChildren && toggleNode(node.id)}
							style={{
								cursor: 'pointer',
							}}>
							<Icon
								icon='caret-right'
								style={{
									transition: 'transform 0.2s',
									transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
								}}
							/>
						</span>
					)}
					<span
						id={node.id}
						onClick={() => onSelect(node, depth)}
						style={{
							cursor: 'pointer',
							fontWeight: selectedEntryId === node.id ? 'bold' : 'normal',
							userSelect: 'none',
						}}>
						{node.name}
					</span>
				</div>
				{isExpanded && node.children && (
					<div>
						{node.children.map((childNode) => renderNode(childNode, depth + 1))}
					</div>
				)}
			</div>
		);
	};

	return <div>{treeData.map((node) => renderNode(node))}</div>;
};

export default Tree;
